<script>
import { useAlert } from 'dashboard/composables';
import CampaignContactGroupsAPI from 'dashboard/api/campaignContactGroups';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'AddContactsToGroupModal',
  components: { Spinner },
  props: {
    selectedContacts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'success'],
  data() {
    return {
      groups: [],
      groupsLoading: false,
      selectedGroup: null,
      addingContacts: false,
      groupContacts: [],
      loadingGroupContacts: false,
    };
  },
  computed: {
    hasSelectedContacts() {
      return this.selectedContacts.length > 0;
    },
    selectedContactsCount() {
      return this.selectedContacts.length;
    },
    contactsAlreadyInGroup() {
      if (!this.selectedGroup || !this.groupContacts.length) {
        return [];
      }
      const groupContactIds = new Set(this.groupContacts.map(contact => contact.id));
      return this.selectedContacts.filter(contact => groupContactIds.has(contact.id));
    },
    contactsToAdd() {
      if (!this.selectedGroup || !this.groupContacts.length) {
        return this.selectedContacts;
      }
      const groupContactIds = new Set(this.groupContacts.map(contact => contact.id));
      return this.selectedContacts.filter(contact => !groupContactIds.has(contact.id));
    },
    hasContactsAlreadyInGroup() {
      return this.contactsAlreadyInGroup.length > 0;
    },
    hasContactsToAdd() {
      return this.contactsToAdd.length > 0;
    },
  },
  mounted() {
    this.fetchGroups();
  },
  watch: {
    selectedGroup(newGroup) {
      if (newGroup) {
        this.loadGroupContacts(newGroup.id);
      } else {
        this.groupContacts = [];
      }
    },
  },
  methods: {
    async fetchGroups() {
      this.groupsLoading = true;
      try {
        const { data } = await CampaignContactGroupsAPI.list();
        this.groups = data.groups || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching groups:', error);
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ERROR_LOAD'));
      } finally {
        this.groupsLoading = false;
      }
    },
    async loadGroupContacts(groupId) {
      this.loadingGroupContacts = true;
      try {
        const { data } = await CampaignContactGroupsAPI.show(groupId);
        this.groupContacts = data.group.contacts || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading group contacts:', error);
        this.groupContacts = [];
      } finally {
        this.loadingGroupContacts = false;
      }
    },
    async addContactsToGroup() {
      if (!this.selectedGroup || !this.hasSelectedContacts) {
        return;
      }

      // Verificar se há contatos já no grupo
      if (this.hasContactsAlreadyInGroup && !this.hasContactsToAdd) {
        // Todos os contatos já estão no grupo
        const contactNames = this.contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = this.contactsAlreadyInGroup.length - contactNames.length;
        let message = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ALL_CONTACTS_ALREADY_IN_GROUP', {
          count: this.contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          message += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            message += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        useAlert(message);
        return;
      }

      // Se há contatos novos e alguns já existem, mostrar mensagem informativa
      if (this.hasContactsAlreadyInGroup && this.hasContactsToAdd) {
        const contactNames = this.contactsAlreadyInGroup
          .map(contact => contact.name || contact.phone_number || contact.email || contact.id)
          .slice(0, 3);
        const moreCount = this.contactsAlreadyInGroup.length - contactNames.length;
        let warningMessage = this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.SOME_CONTACTS_ALREADY_IN_GROUP', {
          count: this.contactsAlreadyInGroup.length,
        });
        if (contactNames.length > 0) {
          warningMessage += `: ${contactNames.join(', ')}`;
          if (moreCount > 0) {
            warningMessage += ` ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.AND_MORE', { count: moreCount })}`;
          }
        }
        warningMessage += `. ${this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADDING_REMAINING', {
          count: this.contactsToAdd.length,
        })}`;
        useAlert(warningMessage);
      }

      // Adicionar apenas os contatos que não estão no grupo
      if (!this.hasContactsToAdd) {
        return;
      }

      this.addingContacts = true;
      try {
        const contactIds = this.contactsToAdd.map(contact => contact.id);
        const payload = {
          campaign_contact_group: {
            add_contact_ids: contactIds,
          },
        };
        await CampaignContactGroupsAPI.update(this.selectedGroup.id, payload);
        useAlert(this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_SUCCESS'));
        // Recarregar contatos do grupo
        await this.loadGroupContacts(this.selectedGroup.id);
        this.$emit('success');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding contacts to group:', error);
        const message =
          error?.response?.data?.error || this.$t('CAMPAIGN.ADD.CONTACTS.GROUPS.ADD_ERROR');
        useAlert(message);
      } finally {
        this.addingContacts = false;
      }
    },
    closeModal() {
      this.selectedGroup = null;
      this.$emit('close');
    },
  },
};
</script>

<template>
  <div class="space-y-4 p-4">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.TITLE')"
      :header-content="$t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.DESCRIPTION', { count: selectedContactsCount })"
    />

    <div v-if="groupsLoading" class="flex items-center justify-center gap-2 p-8">
      <Spinner size="20" />
      <span class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.LOADING') }}
      </span>
    </div>

    <div v-else class="space-y-4">
      <div v-if="!hasSelectedContacts" class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
        <p class="text-sm text-amber-700 dark:text-amber-300">
          {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.NO_CONTACTS') }}
        </p>
      </div>

      <div v-else class="space-y-3">
        <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
          <p class="text-sm text-blue-700 dark:text-blue-300">
            {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.SELECTED_CONTACTS', { count: selectedContactsCount }) }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <label class="woot-form-label">
            {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.SELECT_GROUP_LABEL') }}
          </label>
          <multiselect
            v-model="selectedGroup"
            :options="groups"
            track-by="id"
            label="name"
            :allow-empty="true"
            :searchable="true"
            :loading="groupsLoading"
            :placeholder="$t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.SELECT_GROUP_PLACEHOLDER')"
          />
          <p v-if="!groupsLoading && groups.length === 0" class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.NO_GROUPS') }}
          </p>
          <p v-else class="text-xs text-slate-500 dark:text-slate-400">
            {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.HELP') }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
      <woot-button
        variant="clear"
        type="button"
        @click.prevent="closeModal"
      >
        {{ $t('CAMPAIGN.ADD.CONTACTS.GROUPS.EDIT.CANCEL') }}
      </woot-button>
      <woot-button
        variant="primary"
        type="button"
        :loading="addingContacts"
        :disabled="!selectedGroup || !hasSelectedContacts"
        @click.prevent="addContactsToGroup"
      >
        {{ $t('CAMPAIGN.ADD.CONTACTS.ADD_TO_GROUP_MODAL.ADD_BUTTON') }}
      </woot-button>
    </div>
  </div>
</template>

